import { ReactComponent as AddIcon } from '$icons/Add.svg';
import { ReactComponent as CloseIcon } from '$icons/CloseGreen.svg';
import { ReactComponent as RightArrow } from '$icons/ArrowRight.svg';
import Button from '$components/Buttons/Button';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { ReactComponent as Copy } from '$icons/copy.svg';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetShareCandidateMutation } from '$store/modules/candidates';
import { useGetAllCurrenciesQuery } from '$store/modules/currencies';
import { errorHandler, successHandler } from '$store/utils';
import copyToClipboard from '$utils/copyToClipboard';
import { useForm } from 'react-hook-form';

const DEFAULT_CURRENCY_CODE = 'USD';

type ShareRosterFormType = {
  title: string;
  candidatesIds: string;
  fee: string;
  currencyCode: string;
};

const ShareRoster = () => {
  const [t] = useTranslation('global');
  const [openShareRoster, setOpenShareRoster] = useState(false);
  const [shareRosterUrl, setShareRosterUrl] = useState('');
  const { data: currencies } = useGetAllCurrenciesQuery();
  const { register, handleSubmit, watch, reset } = useForm<ShareRosterFormType>(
    {
      defaultValues: {
        title: '',
        candidatesIds: '',
        fee: '',
        currencyCode: DEFAULT_CURRENCY_CODE,
      },
    }
  );

  const sharedRosterTitle = watch('title');
  const sharedRosterCandidatesIds = watch('candidatesIds');

  const [getShareCandidates] = useGetShareCandidateMutation();

  const closeModalHandler = () => {
    setOpenShareRoster(false);
  };

  const handleGenerateShareRoster = async (payload: ShareRosterFormType) => {
    const ids = payload.candidatesIds
      .replace(/[^,|\d|\n]/g, '')
      ?.split(/,|\n/g)
      ?.filter((singleCandidateId: string) => !!singleCandidateId)
      ?.map((singleId: string) => parseInt(singleId, 10));
    const parsedFee = payload.fee !== '' ? Number(payload.fee) : undefined;
    setShareRosterUrl('Generating URL...');

    await getShareCandidates({
      name: payload.title,
      ids,
      fee: parsedFee,
    }).then(async (result) => {
      if ('data' in result) {
        const rosterUrl = new URL(window.location.origin);
        rosterUrl.pathname = `share/candidates/${result.data.encodedId}`;
        rosterUrl.searchParams.set(
          'currency',
          payload.currencyCode || DEFAULT_CURRENCY_CODE
        );
        setShareRosterUrl(rosterUrl.toString());
        await copyToClipboard(rosterUrl.toString()).then(() => {
          successHandler(
            'Share Candidate Roster URL copied to clipboard',
            true
          );
        });
        reset();
      } else {
        setShareRosterUrl('Error retrieving share roster id, try again later');
        errorHandler(
          '[GetShareCandidates]',
          'Error retrieving share roster id, try again later',
          result.error,
          true
        );
      }
    });
  };

  const copyToClipboardHandler = async () => {
    await copyToClipboard(shareRosterUrl).then(() => {
      successHandler('Share Candidate Roster copied to clipboard', true);
    });
  };

  return (
    <>
      <Button
        bgColor="#006789"
        showText
        text="Generate Roster"
        icon={AddIcon}
        invertTextContent
        styling="px-4 whitespace-nowrap"
        onClick={() => {
          setOpenShareRoster((prev) => !prev);
        }}
      />
      <ModalInTailwind isOpen={openShareRoster} closeModal={closeModalHandler}>
        <form
          onSubmit={handleSubmit(handleGenerateShareRoster)}
          className="flex flex-col bg-white p-8 w-2/5 rounded"
        >
          <header className="flex flex-row justify-between items-center mb-4 w-full">
            <div className="text-xl font-bold whitespace-nowrap">
              {t('ShareRoster.Title')}
            </div>
            <button
              aria-label="Close"
              type="button"
              onClick={closeModalHandler}
            >
              <CloseIcon />
            </button>
          </header>
          <div className="flex flex-col w-full">
            <h3 className="text-lg font-semibold">
              {t('ShareRoster.RosterTitle')}
            </h3>
            <input
              className="border rounded w-full p-2 mb-4"
              placeholder={t('ShareRoster.RosterTitlePlaceholder')}
              {...register('title', { required: true })}
            />
            <h3 className="text-lg font-semibold">
              {t('ShareRoster.CandidateIds')}
            </h3>
            <textarea
              data-testid="video-prompt-textarea"
              className="border rounded w-full min-h-20 p-2 mb-4"
              placeholder={t('ShareRoster.CandidateIdsPlaceholder')}
              {...register('candidatesIds', {
                required: true,
              })}
            />
            <h3 className="text-lg font-semibold">{t('ShareRoster.Fee')}</h3>
            <input
              className="border rounded w-full p-2 mb-4"
              type="number"
              min={0}
              step="any"
              placeholder={t('ShareRoster.FeePlaceholder')}
              {...register('fee')}
            />
            <h3 className="text-lg font-semibold">
              {t('ShareRoster.DisplayCurrency')}
            </h3>
            <select
              className="border rounded w-full p-2 mb-4"
              {...register('currencyCode')}
            >
              {currencies?.map((c) => (
                <option key={c.Code} value={c.Code}>
                  {c.Name}
                </option>
              ))}
            </select>
            <div className="flex flex-row">
              <input
                className="border rounded-l w-full p-2"
                placeholder={t('ShareRoster.GeneratedUrlPlaceholder')}
                readOnly
                disabled
                value={shareRosterUrl}
              />
              <button
                disabled={!shareRosterUrl}
                onClick={copyToClipboardHandler}
                type="button"
                className="flex flex-row gap-2 items-center border rounded-r border-gray-200 border-l-0 border-solid text-gray-500 px-2 disabled:cursor-not-allowed"
              >
                {t('ShareRoster.Copy')} <Copy className="opacity-40" />
              </button>
            </div>
            <div className="flex flex-row mt-4 self-end w-60">
              <Button
                text={t('ShareRoster.Cancel')}
                showText
                textStyles={{ color: '#079e88' }}
                onClick={closeModalHandler}
              />
              <Button
                text={t('ShareRoster.Generate')}
                icon={RightArrow}
                invertTextContent
                showText
                bgColor="#079e88"
                disabled={
                  !sharedRosterTitle || sharedRosterCandidatesIds.length === 0
                }
                customStyles={
                  (!sharedRosterTitle ||
                    sharedRosterCandidatesIds.length === 0) && {
                    opacity: '.25',
                  }
                }
                submit
              />
            </div>
          </div>
        </form>
      </ModalInTailwind>
    </>
  );
};
export default ShareRoster;
